/**
 * API layer for AED AI Analysis Review app.
 * Integrates with AutoPageScraper (readisys) backend:
 * - GET /api/review/devices, /api/review/stats
 * - GET /api/subscribers
 * - GET /api/aed-images/download
 * - POST /api/devices/{subscriber}/{sentinel_id}/refresh-analysis
 *
 * Set REACT_APP_BACKEND_URL to your backend (e.g. http://localhost:8000 for dev).
 * Auth: set access_token in localStorage or use login; requests send Authorization: Bearer <token>.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Send auth token if present (from readisys login)
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Backend detailed_status → our UI status key (Expired B/P = one case for battery or pads)
const DETAILED_TO_UI_STATUS = {
  'READY': 'READY',
  'NOT READY': 'NOT_READY',
  'REPOSITION': 'REPOSITION',
  'NOT PRESENT': 'AED_NOT_PRESENT',
  'EXPIRED B/P': 'BATTERY_EXPIRED',
  'BATTERY EXPIRED': 'BATTERY_EXPIRED',
  'PADS EXPIRED': 'BATTERY_EXPIRED',
  'EXPIRED PADS': 'BATTERY_EXPIRED',
  'EXPIRING BATT/PADS': 'EXPIRING_BATT_PADS',
  'CAMERA ISSUE': 'CAMERA_ISSUE',
  'UNKNOWN': 'UNKNOWN',
  'LOST CONTACT': 'LOST_CONTACT'
};

// Normalize API detailed_status for lookup: trim, uppercase, underscore → space (DB has canonical values)
function normalizeBackendStatus(s) {
  if (s == null || typeof s !== 'string') return null;
  const t = String(s).trim().toUpperCase().replace(/_/g, ' ');
  return t || null;
}

// Map our filter status → canonical value for GET /api/review/devices.
// Backend normalizes (e.g. underscores → spaces), then queries MongoDB: strict match on
// detailed_status for NOT READY; other statuses may fallback to dashboard status when missing.
const UI_TO_DETAILED_STATUS = {
  'READY': 'READY',
  'NOT_READY': 'NOT READY',
  'REPOSITION': 'REPOSITION',
  'AED_NOT_PRESENT': 'NOT PRESENT',
  'BATTERY_EXPIRED': 'EXPIRED B/P',
  'EXPIRING_BATT_PADS': 'EXPIRING BATT/PADS',
  'CAMERA_ISSUE': 'CAMERA ISSUE',
  'UNKNOWN': 'UNKNOWN',
  'LOST_CONTACT': 'LOST CONTACT'
};

function deviceToAED(device) {
  const conf = device.detailed_status_confidence != null ? device.detailed_status_confidence : (device.aed_presence_confidence ?? device.ai_confidence ?? 0);
  const location = device.location ?? ([device.site, device.building, device.placement].filter(Boolean).join(' - ') || '—');
  // Use API detailed_status from database (no regex; map via canonical lookup)
  const rawStatus = device.detailed_status ?? device.detailedStatus;
  const canonical = normalizeBackendStatus(rawStatus);
  const status = (canonical && DETAILED_TO_UI_STATUS[canonical])
    ? DETAILED_TO_UI_STATUS[canonical]
    : (device.current_status === 'OFFLINE' ? 'LOST_CONTACT' : 'UNKNOWN');
  const sentinelId = device.sentinel_id ?? device.aed_id ?? device.id;
  const imageUrl = device.s3_url
    ? `${API}/aed-images/download?url=${encodeURIComponent(device.s3_url)}`
    : (device.last_image_url && (device.last_image_url.startsWith('http') || device.last_image_url.startsWith('/')))
      ? device.last_image_url
      : null;
  return {
    id: `${device.subscriber}|${sentinelId}`,
    aed_id: sentinelId,
    sentinel_id: sentinelId,
    subscriber: device.subscriber,
    location,
    current_status: status,
    ai_confidence: conf,
    review_required: device.review_required ?? conf < 0.85,
    updated_at: device.detailed_status_analyzed_at || device.captured_at || device.updated_at,
    last_image_url: imageUrl,
    last_analysis: {
      reason: device.detailed_status_explanation || device.aed_presence_reasoning || (device.last_analysis?.reason ?? ''),
      detected_aed: device.aed_present ?? (status !== 'AED_NOT_PRESENT'),
      aed_present_confidence: device.aed_presence_confidence ?? device.last_analysis?.aed_present_confidence ?? conf,
      led_visible: device.last_analysis?.led_visible ?? null,
      led_visible_confidence: device.last_analysis?.led_visible_confidence ?? (device.led_visible_confidence ?? 0),
      led_state: device.last_analysis?.led_state ?? device.led_state ?? null,
      led_color_confidence: device.last_analysis?.led_color_confidence ?? (device.led_color_confidence ?? 0),
      expiration_detected_confidence: device.last_analysis?.expiration_detected_confidence ?? (device.expiration_detected_confidence ?? 0)
    }
  };
}

// When filtering by UNKNOWN, backend may use UNKNOWN only (NOT READY and CAMERA ISSUE are now separate)
const DETAILED_STATUSES_FOR_UNKNOWN = ['UNKNOWN'];

const DEFAULT_PAGE_SIZE = 30;

// AED APIs
export const aedApi = {
  getAll: async (filters = {}, options = {}) => {
    const limit = options.limit != null ? options.limit : 500;
    const skip = options.skip != null ? options.skip : 0;

    const buildParams = (detailedStatus) => {
      const params = new URLSearchParams();
      if (filters.subscriber) params.append('subscriber', String(filters.subscriber).trim());
      if (detailedStatus) params.append('detailed_status', detailedStatus);
      if (filters.reviewRequired !== null && filters.reviewRequired !== undefined) {
        params.append('review_required', String(filters.reviewRequired));
      }
      if (filters.minConfidence != null) params.append('min_confidence', String(filters.minConfidence));
      if (filters.maxConfidence != null) params.append('max_confidence', String(filters.maxConfidence));
      if (filters.search) params.append('search', filters.search || '');
      params.append('limit', String(limit));
      params.append('skip', String(skip));
      return params;
    };

    const detailedStatus = filters.status ? UI_TO_DETAILED_STATUS[filters.status] : null;
    const isUnknownFilter = filters.status === 'UNKNOWN';

    try {
      let devices = [];
      if (isUnknownFilter) {
        const seen = new Set();
        for (const ds of DETAILED_STATUSES_FOR_UNKNOWN) {
          const params = buildParams(ds);
          const response = await apiClient.get(`/review/devices?${params.toString()}`);
          const data = response.data || {};
          const list = Array.isArray(data) ? data : (data.devices ?? data.data ?? []);
          for (const d of list) {
            const id = d.sentinel_id ?? d.aed_id ?? d.id;
            const key = id ? `${d.subscriber}|${id}` : JSON.stringify(d);
            if (!seen.has(key)) {
              seen.add(key);
              devices.push(d);
            }
          }
        }
      } else {
        const params = buildParams(detailedStatus);
        const response = await apiClient.get(`/review/devices?${params.toString()}`);
        const data = response.data || {};
        devices = Array.isArray(data) ? data : (data.devices ?? data.data ?? []);
      }
      const aeds = devices.map(deviceToAED);
      // Backend already filtered by detailed_status; if response omits detailed_status on devices, treat them as the requested status so list matches overview count.
      if (filters.status && detailedStatus && devices.length > 0) {
        aeds.forEach((aed) => {
          if (aed.current_status === 'UNKNOWN') aed.current_status = filters.status;
        });
      }
      if (filters.status) {
        return aeds.filter((aed) => aed.current_status === filters.status);
      }
      return aeds;
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 501) {
        const paramsAeds = new URLSearchParams();
        if (filters.subscriber) paramsAeds.append('subscriber', String(filters.subscriber).trim());
        if (filters.status) paramsAeds.append('status', filters.status);
        if (filters.reviewRequired !== null && filters.reviewRequired !== undefined) {
          paramsAeds.append('review_required', String(filters.reviewRequired));
        }
        if (filters.minConfidence != null) paramsAeds.append('min_confidence', String(filters.minConfidence));
        if (filters.maxConfidence != null) paramsAeds.append('max_confidence', String(filters.maxConfidence));
        if (filters.search) paramsAeds.append('search', filters.search);
        paramsAeds.append('limit', String(limit));
        paramsAeds.append('skip', String(skip));
        const res = await apiClient.get(`/aeds?${paramsAeds.toString()}`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.devices ?? res.data?.data ?? []);
        const aeds = list.map(deviceToAED);
        if (filters.status) {
          return aeds.filter((aed) => aed.current_status === filters.status);
        }
        return aeds;
      }
      throw e;
    }
  },

  getSubscribers: async () => {
    const response = await apiClient.get('/subscribers');
    const raw = Array.isArray(response.data) ? response.data : (response.data?.subscribers || []);
    return raw.map(s => typeof s === 'string' ? s : (s?.name ?? s?.subscriber ?? s?.id ?? String(s)));
  },

  getById: async (id) => {
    const [sub, sentinelId] = id.includes('|') ? id.split('|') : [null, id];
    const params = new URLSearchParams();
    if (sub) params.append('subscriber', sub);
    if (sentinelId) params.append('sentinel_id', sentinelId);
    params.append('limit', '1');
    const response = await apiClient.get(`/review/devices?${params.toString()}`);
    const devices = response.data.devices || [];
    if (devices.length === 0) return null;
    return deviceToAED(devices[0]);
  },

  getHistory: async (aedId) => {
    // No image history endpoint yet; return empty
    return [];
  },

  analyzeImage: async (aedId, file) => {
    const [subscriber, sentinelId] = aedId.includes('|') ? aedId.split('|') : [null, aedId];
    if (!subscriber || !sentinelId) {
      throw new Error('Invalid AED id');
    }
    await apiClient.post(`/devices/${encodeURIComponent(subscriber)}/${encodeURIComponent(sentinelId)}/refresh-analysis`);
    const aed = await aedApi.getById(aedId);
    return { aed };
  },

  confirm: async (aedId, comment = '') => {
    // No backend yet; no-op
    return { success: true };
  },

  override: async (aedId, newStatus, comment) => {
    return { success: true };
  },

  escalate: async (aedId, comment = '') => {
    return { success: true };
  }
};

// Stats: Status Overview counts come from GET /api/review/stats (backend is source of truth).
// We map backend status_counts keys to our UI keys; e.g. NOT_READY = backend "NOT READY" count.
function normalizeStats(d) {
  if (!d) return { total_aeds: 0, status_counts: {}, review_required: 0, low_confidence_count: 0, reposition_rate: 0 };
  const status_counts = {
    READY: d.status_counts?.READY ?? 0,
    NOT_READY: d.status_counts?.['NOT READY'] ?? 0,
    REPOSITION: d.status_counts?.REPOSITION ?? 0,
    AED_NOT_PRESENT: d.status_counts?.['NOT PRESENT'] ?? 0,
    BATTERY_EXPIRED: (d.status_counts?.['EXPIRED B/P'] ?? 0) + (d.status_counts?.['PADS EXPIRED'] ?? 0),
    EXPIRING_BATT_PADS: d.status_counts?.['EXPIRING BATT/PADS'] ?? 0,
    CAMERA_ISSUE: d.status_counts?.['CAMERA ISSUE'] ?? 0,
    UNKNOWN: d.status_counts?.UNKNOWN ?? 0,
    LOST_CONTACT: d.status_counts?.['LOST CONTACT'] ?? d.status_counts?.LOST_CONTACT ?? 0
  };
  if (status_counts.AED_NOT_PRESENT === 0 && (d.status_counts?.['AED_NOT_PRESENT'] != null)) {
    status_counts.AED_NOT_PRESENT = d.status_counts['AED_NOT_PRESENT'];
  }
  if (status_counts.LOST_CONTACT === 0 && (d.status_counts?.['OFFLINE'] != null)) {
    status_counts.LOST_CONTACT = d.status_counts.OFFLINE;
  }
  return {
    total_aeds: d.total_aeds ?? 0,
    status_counts,
    review_required: d.review_required ?? 0,
    low_confidence_count: d.low_confidence_count ?? 0,
    reposition_rate: d.reposition_rate ?? 0
  };
}

export const statsApi = {
  get: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.subscriber) params.append('subscriber', String(filters.subscriber).trim());
    if (filters.status) params.append('detailed_status', UI_TO_DETAILED_STATUS[filters.status] || filters.status);
    if (filters.reviewRequired === true) params.append('review_status', 'needs_review');
    if (filters.reviewRequired === false) params.append('review_status', 'reviewed');
    const query = params.toString() ? `?${params.toString()}` : '';
    try {
      const response = await apiClient.get(`/review/stats${query}`);
      return normalizeStats(response.data);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 501) {
        const paramsStats = new URLSearchParams();
        if (filters.subscriber) paramsStats.append('subscriber', String(filters.subscriber).trim());
        if (filters.reviewRequired === true) paramsStats.append('review_required', 'true');
        if (filters.reviewRequired === false) paramsStats.append('review_required', 'false');
        const response = await apiClient.get(`/stats${paramsStats.toString() ? `?${paramsStats.toString()}` : ''}`);
        const d = response.data || {};
        return normalizeStats({
          ...d,
          status_counts: {
            'READY': d.status_counts?.READY ?? 0,
            'NOT READY': d.status_counts?.NOT_READY ?? 0,
            'REPOSITION': d.status_counts?.REPOSITION ?? 0,
            'NOT PRESENT': d.status_counts?.AED_NOT_PRESENT ?? 0,
            'EXPIRED B/P': d.status_counts?.BATTERY_EXPIRED ?? 0,
            'EXPIRING BATT/PADS': d.status_counts?.EXPIRING_BATT_PADS ?? 0,
            'CAMERA ISSUE': d.status_counts?.CAMERA_ISSUE ?? 0,
            'UNKNOWN': d.status_counts?.UNKNOWN ?? 0,
            'LOST CONTACT': d.status_counts?.LOST_CONTACT ?? d.status_counts?.OFFLINE ?? 0
          }
        });
      }
      throw e;
    }
  }
};

export const auditApi = {
  getAll: async () => {
    return [];
  }
};

export default apiClient;
